import React, { useState, useEffect } from "react";
import { dashboardAPI } from "../../Api/dashboardAPI";
import { NovedadesMasivasResponse } from "../../interfaces/novedadesMasivas";
import ConfigurableTable, {
  ColumnConfig,
  ActionButton,
} from "../../components/ConfigurableTable/ConfigurableTable";
import Pagination from "../../components/reusable/Pagination/Pagination";
import Input from "../../components/reusable/Input/Input";
import Button from "../../components/reusable/Button/Button";
import PageLoader from "../../components/reusable/PageLoader/PageLoader";
import ModalConfirmation from "../../components/reusable/ModalConfirmation/ModalConfirmation";
import { toastSuccess, toastError } from "../../utils/toast";
import SlSpinner from "@shoelace-style/shoelace/dist/react/spinner";

const NovedadesMasivas = () => {
  const [novedades, setNovedades] = useState<NovedadesMasivasResponse[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState("TODOS");
  const [idNovedadInicio, setIdNovedadInicio] = useState<number | null>(null);
  const [idNovedadFin, setIdNovedadFin] = useState<number | null>(null);
  const [confirmationModal, setConfirmationModal] = useState<{
    show: boolean;
    novedad: NovedadesMasivasResponse | null;
  }>({
    show: false,
    novedad: null,
  });

  const pageSize = 10;

  const estados = [
    "TODOS",
    "REGISTRADA",
    "EN_CURSO",
    "FINALIZADA_OK",
    "FINALIZADA_ERROR",
    "FINALIZADA_INVALIDA",
  ];

  // Función para formatear fechas
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("es-AR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Función para obtener el badge de estado
  const getEstadoBadge = (estado: string) => {
    const estadoClasses = {
      REGISTRADA: "bg-blue-50 text-blue-600",
      EN_CURSO: "bg-yellow-50 text-yellow-600",
      FINALIZADA_OK: "bg-green-50 text-green-600",
      FINALIZADA_ERROR: "bg-red-50 text-red-600",
      FINALIZADA_INVALIDA: "bg-orange-50 text-orange-600",
    };

    const estadoLabels = {
      REGISTRADA: "Registrada",
      EN_CURSO: "En Curso",
      FINALIZADA_OK: "Finalizada OK",
      FINALIZADA_ERROR: "Finalizada Error",
      FINALIZADA_INVALIDA: "Finalizada Inválida",
    };

    // Si el estado es EN_CURSO, mostrar loader junto con el texto
    if (estado === "EN_CURSO") {
      return (
        <span
          className={`rounded-full text-xs align-middle font-medium px-2 py-1 flex items-center gap-2 ${
            estadoClasses[estado as keyof typeof estadoClasses] ||
            "bg-gray-50 text-gray-600"
          }`}
        >
          <SlSpinner style={{ fontSize: "0.75rem" }} />
          {estadoLabels[estado as keyof typeof estadoLabels] || estado}
        </span>
      );
    }

    return (
      <span
        className={`rounded-full text-xs align-middle font-medium px-2 py-1 ${
          estadoClasses[estado as keyof typeof estadoClasses] ||
          "bg-gray-50 text-gray-600"
        }`}
      >
        {estadoLabels[estado as keyof typeof estadoLabels] || estado}
      </span>
    );
  };

  // Configuración de columnas para ConfigurableTable
  const columns: ColumnConfig[] = [
    {
      key: "id",
      title: "ID",
      width: "60px",
      align: "center",
    },
    {
      key: "tipoEvento",
      title: "Tipo Evento",
      width: "130px",
      align: "center",
    },
    {
      key: "accion",
      title: "Acción",
      width: "50px",
      align: "center",
    },
    {
      key: "idNovedadInicio",
      title: "ID Novedad Inicio",
      width: "100px",
      align: "center",
    },
    {
      key: "idNovedadFin",
      title: "ID Novedad Fin",
      width: "100px",
      align: "center",
    },

    {
      key: "estado",
      title: "Estado",
      width: "140px",
      align: "center",
      render: (value) => getEstadoBadge(value),
    },
    {
      key: "mensajesPendientes",
      title: "Mensajes asíncronos pendientes",
      width: "100px",
      align: "center",
    },
    {
      key: "timestampInicio",
      title: "Fecha Inicio",
      width: "90px",
      align: "center",
      render: (value) => formatDate(value),
    },
    {
      key: "timestampFin",
      title: "Fecha Fin",
      width: "90px",
      align: "center",
      render: (value) => formatDate(value),
    },
    /*     {
      key: 'timeout',
      title: 'Timeout (min)',
      width: '120px',
      align: 'center',
    }, */
    {
      key: "contadorInicios",
      title: "Contador Inicios",
      width: "50px",
      align: "center",
    },
    {
      key: "resultado",
      title: "Resultado",
      width: "400px",
      align: "left",
      render: (value) => (
        <div
          className="max-w-[400px] max-h-[70px] overflow-auto text-sm p-2 bg-gray-50 rounded border"
          style={{
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          {value || "N/A"}
        </div>
      ),
    },
  ];

  // Configuración de botones de acción
  const actionButtons: ActionButton[] = [
    {
      label: "Reprocesar",
      iconName: "arrow-clockwise",
      theme: "primary",
      size: "small",
      onClick: (novedad: NovedadesMasivasResponse) => handleReprocesar(novedad),
      disabled: (novedad: NovedadesMasivasResponse) =>
        novedad.estado === "EN_CURSO",
      loading: (novedad: NovedadesMasivasResponse) =>
        loadingAction === `reprocesar-${novedad.id}`,
/*       show: (novedad: NovedadesMasivasResponse) =>
        novedad.estado !== "EN_CURSO", */
    },
  ];

  const fetchNovedades = async (page: number) => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getNovedadesMasivasPaginated(
        pageSize,
        (page - 1) * pageSize,
        estadoSeleccionado,
        idNovedadInicio || null,
        idNovedadFin || null
      );
      console.log("fetch novedades masivas response", response);
      setNovedades(response.data as NovedadesMasivasResponse[]);
      setTotalCount(response.totalCount);
    } catch (error) {
      console.error("Error al cargar novedades masivas:", error);
      toastError("Error al cargar las novedades masivas");
    } finally {
      setLoading(false);
    }
  };

  const fetchReprocesarNovedadMasiva = async (idNovedadMasiva: number) => {
    try {
      setLoadingAction(`reprocesar-${idNovedadMasiva}`);
      const response = await dashboardAPI.reprocesarNovedadMasiva(
        idNovedadMasiva
      );
      console.log("fetch reprocesar novedad masiva response", response);
      toastSuccess(`Reprocesando novedad masiva ID: ${idNovedadMasiva}`);
      // Refrescar la lista después del reprocesamiento
      await fetchNovedades(currentPage);
    } catch (error) {
      console.error("Error al reprocesar novedad masiva:", error);
      toastError("Error al reprocesar la novedad masiva");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleReprocesar = (novedad: NovedadesMasivasResponse) => {
    setConfirmationModal({
      show: true,
      novedad: novedad,
    });
  };

  const confirmReprocesar = async () => {
    const novedad = confirmationModal.novedad;
    setConfirmationModal({ show: false, novedad: null });
    if (!novedad) return;

    try {
      setLoadingAction(`reprocesar-${novedad.id}`);
      // Aquí iría la llamada a la API para reprocesar
      console.log("Reprocesando novedad:", novedad);

      // Simular llamada a API
      await fetchReprocesarNovedadMasiva(novedad.id);
      // Refrescar la lista después del reprocesamiento
      await fetchNovedades(currentPage);
    } catch (error) {
      console.error("Error al reprocesar novedad:", error);
      toastError("Error al reprocesar la novedad");
    } finally {
      setLoadingAction(null);
      setConfirmationModal({ show: false, novedad: null });
    }
  };

  const cancelReprocesar = () => {
    setConfirmationModal({ show: false, novedad: null });
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleEstadoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEstadoSeleccionado(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchNovedades(currentPage);
  }, [currentPage, estadoSeleccionado]);

  return (
    <>
      {loading && <PageLoader />}
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            Novedades Masivas
          </h1>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Total: {totalCount} registros</span>
          </div>
        </div>

        {/* Panel de filtros mejorado */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">
            Filtros de Búsqueda
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado
              </label>
              <select
                value={estadoSeleccionado}
                onChange={handleEstadoChange}
                className="w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {estados.map((estado) => (
                  <option key={estado} value={estado}>
                    {estado}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID Novedad Inicio
              </label>
              <Input
                placeholder="Ej: 1000"
                value={idNovedadInicio ? String(idNovedadInicio) : ""}
                onChange={(value) =>
                  setIdNovedadInicio(value ? Number(value) : null)
                }
                type="number"
                size="medium"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID Novedad Fin
              </label>
              <Input
                placeholder="Ej: 2000"
                value={idNovedadFin ? String(idNovedadFin) : ""}
                onChange={(value) =>
                  setIdNovedadFin(value ? Number(value) : null)
                }
                type="number"
                size="medium"
              />
            </div>

            <div className="flex items-end">
              <Button
                label="Actualizar"
                iconName="arrow-clockwise"
                theme="primary"
                onClick={() => fetchNovedades(1)}
                className="w-full"
              />
            </div>

            <div className="flex items-end">
              <Button
                label="Limpiar Filtros"
                iconName="eraser"
                theme="default"
                onClick={() => {
                  setEstadoSeleccionado("TODOS");
                  setIdNovedadInicio(null);
                  setIdNovedadFin(null);
                  setCurrentPage(1);
                }}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Tabla de novedades */}
        <div className="bg-white rounded-lg shadow-sm border">
          <ConfigurableTable
            data={novedades}
            columns={columns}
            actionButtons={actionButtons}
            /* loading={loading} */
            emptyMessage="No se encontraron novedades masivas"
            striped={true}
            hoverable={true}
            className="w-full"
          />

          {/* Paginación */}
          {totalCount > pageSize && (
            <div className="border-t p-4">
              <Pagination
                totalCount={totalCount}
                onPageChange={handleChangePage}
                currentPage={currentPage}
                pageSize={pageSize}
              />
            </div>
          )}
        </div>

        {/* Modal de confirmación para reprocesar */}
        <ModalConfirmation
          show={confirmationModal.show}
          title="Confirmar Reprocesamiento"
          message={`¿Está seguro que desea reprocesar la novedad ID: ${confirmationModal.novedad?.id}?\n\nTipo: ${confirmationModal.novedad?.tipoEvento}\nAcción: ${confirmationModal.novedad?.accion}\nEstado actual: ${confirmationModal.novedad?.estado}`}
          onClose={cancelReprocesar}
          onConfirm={confirmReprocesar}
          topBorderWarn={true}
        />
      </div>
    </>
  );
};

export default NovedadesMasivas;
